/*
 * Home (CLAUDE.md §4, Saif 19 and 21 Oct 2026; docs/23 "Home"): GET /api/home sends the sections that are on, in Saif's order, and
 * the What's new cards for this customer (already filtered and in order). Until it is deployed the app uses the starting
 * order with no cards. "Your usual" is built here, from the last order and the favourites.
 */
import { api } from './api.js';
import { DEFAULT_HOME as SHARED_DEFAULT, HOME_SECTION_IDS, type HomeSectionId } from '@tikkas/shared';

export type HomeBlockId = HomeSectionId;
export interface WhatsNewCard { id: string; title: string; text: string; imageUrl: string | null; link: string | null }
export interface HomeDto { blocks: { id: HomeBlockId; enabled: boolean }[]; whatsNew: WhatsNewCard[] }

/** The starting order (CLAUDE.md §4; shared appearance/home.ts), no cards. */
export const DEFAULT_HOME: HomeDto = { blocks: SHARED_DEFAULT.blocks.map((b) => ({ ...b })), whatsNew: [] };

const KNOWN = HOME_SECTION_IDS as readonly HomeBlockId[];

/**
 * Read /api/home leniently: `blocks` lists only the enabled ones ([{id}]; an `enabled: false` is honoured too), unknown
 * ids are skipped; `cards` have id, title, body, imageUrl, link. A missing or broken answer falls back to the starting order.
 */
export function parseHome(raw: unknown): HomeDto {
  const r = (raw ?? {}) as { blocks?: unknown; cards?: unknown; whatsNew?: unknown };
  const blocks = Array.isArray(r.blocks)
    ? r.blocks.map((b) => (typeof b === 'string' ? { id: b, enabled: true } : (b ?? {}) as { id?: unknown; enabled?: unknown }))
      .filter((b): b is { id: HomeBlockId; enabled: unknown } => KNOWN.includes(b.id as HomeBlockId))
      .map((b) => ({ id: b.id, enabled: b.enabled !== false }))
    : DEFAULT_HOME.blocks;
  const list = Array.isArray(r.cards) ? r.cards : Array.isArray(r.whatsNew) ? r.whatsNew : [];
  const cards = list.map((c) => (c ?? {}) as Record<string, unknown>).filter((c) => typeof c.id === 'string' && typeof c.title === 'string')
    .map((c) => ({ id: c.id as string, title: c.title as string, text: typeof c.body === 'string' ? c.body : typeof c.text === 'string' ? c.text : '', imageUrl: typeof c.imageUrl === 'string' ? c.imageUrl : null, link: typeof c.link === 'string' ? c.link : null }));
  return { blocks: blocks.length ? blocks : DEFAULT_HOME.blocks, whatsNew: cards };
}

/**
 * The last layout the server gave, kept for the session and in storage, so Home draws its sections in the right order and
 * with the right switches from its very first frame (Saif, 22 Oct: nothing above the card may move once it has painted).
 */
const KEPT_KEY = 'tikkas.home';
let kept: HomeDto | null = null;
export function keptHome(): HomeDto | null {
  if (kept) return kept;
  try {
    const raw = localStorage.getItem(KEPT_KEY);
    if (raw) kept = parseHome(JSON.parse(raw));
  } catch { /* storage off */ }
  return kept;
}
export async function loadHome(): Promise<HomeDto> {
  try {
    const dto = parseHome(await api<unknown>('/api/home'));
    kept = dto;
    try { localStorage.setItem(KEPT_KEY, JSON.stringify(dto)); } catch { /* storage off */ }
    return dto;
  } catch {
    return kept ?? DEFAULT_HOME;
  }
}
